import { createSelector, createSlice } from '@reduxjs/toolkit';
import { put, select, take, takeLatest } from 'redux-saga/effects';
import env from '@beam-australia/react-env';
import {
  getAllGlobalContentTypesInitialize,
  getAllHearingRolesInitialize,
  getAllHearingsInitialize,
  getAllHearingStatusInitialize,
  getAllUsersInitialize,
  getAllHearingTemplatesInitialize,
  getVersionInitialize,
  getAllSubjectAreasInitialize,
  getAllCityAreasInitialize,
} from './actions/api';
import {
  currentUserIsAdministratorSelector,
  currentUserIsHearingCreatorSelector,
  isAuthorizedSelector,
  loginFlowFinalized,
  loginFlowInitialize,
} from './authenticationSlice';

function isUrlValid(string) {
  try {
    new URL(string);
    return true;
  } catch (err) {
    return false;
  }
}

// -------- Slice --------
const rootSlice = createSlice({
  name: 'root',
  initialState: {
    isAppReady: false,
    baseApiUrl: null,
    paginateHearings: false,
    paginateComments: false,
    theme: 'novataris',
  },
  reducers: {
    bootstrapInitialize(_) {},
    bootstrapStarted(state) {
      state.paginateHearings = env('EXT_PAGINATE_HEARINGS') === 'true';
      state.paginateComments = env('EXT_PAGINATE_COMMENTS') === 'true';
      state.theme = env('EXT_THEME');
      state.baseApiUrl = env('EXT_BASE_API_URL');
      if (typeof state.baseApiUrl === 'undefined') {
        console.warn(
          'There was no url set for base api so no calls to the api will succeed. Did you forget to set the REACT_APP_EXT_BASE_API_URL in .env.local?',
        );
      } else if (!isUrlValid(state.baseApiUrl)) {
        console.warn(
          `The base url of the api is not a valid url. Are you sure this is the right url? '${state.baseApiUrl}'`,
        );
      }
    },
    bootstrapCompleted(state) {
      state.isAppReady = true;
    },
  },
});

// -------- Selectors --------
const selectIsAppReady = (state) => state.root.isAppReady;
const selectBaseApiUrl = (state) => state.root.baseApiUrl;
const selectPaginateHearings = (state) => state.root.paginateHearings;
const selectPaginateComments = (state) => state.root.paginateComments;
const selectTheme = (state) => state.root.theme;

const isAppReadySelector = createSelector(
  selectIsAppReady,
  (isAppReady) => isAppReady,
);
const baseApiUrlSelector = createSelector(
  selectBaseApiUrl,
  (baseApiUrl) => baseApiUrl,
);

const shouldPaginateHearingsSelector = createSelector(
  selectPaginateHearings,
  (paginateHearings) => paginateHearings,
);

const shouldPaginateCommentsSelector = createSelector(
  selectPaginateComments,
  (paginateComments) => paginateComments,
);

const themeSelector = createSelector(selectTheme, (theme) => theme);

// -------- Sagas --------
export function* bootstrapSaga() {
  yield takeLatest(bootstrapInitialize.type, doBootstrap);
}

function* doBootstrap() {
  yield put(rootSlice.actions.bootstrapStarted());

  yield put(getVersionInitialize());
  yield put(loginFlowInitialize());
  yield take(loginFlowFinalized.type);

  const isAuthorized = yield select(isAuthorizedSelector);
  const isAdministrator = yield select(currentUserIsAdministratorSelector);
  const isHearingCreator = yield select(currentUserIsHearingCreatorSelector);
  const paginateHearings = yield select(shouldPaginateHearingsSelector);

  if (isAuthorized) {
    yield put(getAllHearingStatusInitialize());
    yield put(getAllHearingRolesInitialize());
    yield put(getAllSubjectAreasInitialize());
    yield put(getAllCityAreasInitialize());

    if (!paginateHearings) {
      yield put(getAllHearingsInitialize());
    }

    yield put(getAllGlobalContentTypesInitialize());
    yield put(getAllHearingTemplatesInitialize());
  }
  if (isAdministrator || isHearingCreator) {
    yield put(getAllUsersInitialize());
  }
  yield put(rootSlice.actions.bootstrapCompleted());
}

// -------- Exporting --------
export const { bootstrapInitialize, bootstrapCompleted } = rootSlice.actions;
export {
  isAppReadySelector,
  baseApiUrlSelector,
  shouldPaginateHearingsSelector,
  shouldPaginateCommentsSelector,
  themeSelector,
};
export default rootSlice.reducer;
